// =============================================================================
// New Channel / Conversation Dialog
// Unified dialog for creating DMs, group conversations, and scope channels
// =============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, MessageCircle, Users, Hash, X, Check,
  Megaphone, Zap, BookOpen, Lightbulb, HelpCircle,
  Bug, Rocket, Shield, Heart, Star, Bell, Coffee,
  Target, Flag, Compass, Award, Flame, Globe, Lock,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useChatStore } from '../hooks/useChatStore';
import { createGroupConversation, fetchChannelScopes } from '../data/channelRepository';
import { fetchOrganizationMembers } from '@/modules/core/data/memberRepository';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// ─── ICON OPTIONS ─────────────────────────────────────────────────────────
const CHANNEL_ICONS: { name: string; icon: LucideIcon; label: string }[] = [
  { name: 'hash', icon: Hash, label: 'Par défaut' },
  { name: 'megaphone', icon: Megaphone, label: 'Annonces' },
  { name: 'zap', icon: Zap, label: 'Zap' },
  { name: 'book-open', icon: BookOpen, label: 'Docs' },
  { name: 'lightbulb', icon: Lightbulb, label: 'Idées' },
  { name: 'help-circle', icon: HelpCircle, label: 'Aide' },
  { name: 'bug', icon: Bug, label: 'Bugs' },
  { name: 'rocket', icon: Rocket, label: 'Lancement' },
  { name: 'shield', icon: Shield, label: 'Sécurité' },
  { name: 'heart', icon: Heart, label: 'Social' },
  { name: 'star', icon: Star, label: 'Étoile' },
  { name: 'bell', icon: Bell, label: 'Notifs' },
  { name: 'coffee', icon: Coffee, label: 'Pause' },
  { name: 'target', icon: Target, label: 'Objectifs' },
  { name: 'flag', icon: Flag, label: 'Drapeau' },
  { name: 'compass', icon: Compass, label: 'Explorer' },
  { name: 'award', icon: Award, label: 'Récompense' },
  { name: 'flame', icon: Flame, label: 'Tendance' },
  { name: 'globe', icon: Globe, label: 'Public' },
  { name: 'lock', icon: Lock, label: 'Privé' },
];

interface MemberOption {
  member_id: string;
  full_name: string | null;
  avatar_url: string | null;
  email: string;
}

type CreationType = 'dm' | 'group' | 'channel';

interface NewChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: CreationType;
}

export function NewChannelDialog({ open, onOpenChange, defaultTab = 'dm' }: NewChannelDialogProps) {
  const { currentOrganization, currentRole } = useOrganization();
  const isAdmin = currentRole === 'owner' || currentRole === 'admin';
  const { createDM, createNewChannel, setActiveChannel, loadMessages, loadChannels, currentMemberId } = useChatStore();

  const [tab, setTab] = useState<CreationType>(defaultTab);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // DM state
  const [selectedDmMember, setSelectedDmMember] = useState<MemberOption | null>(null);

  // Group state
  const [selectedGroupMembers, setSelectedGroupMembers] = useState<MemberOption[]>([]);
  const [groupName, setGroupName] = useState('');

  // Channel state
  const [channelName, setChannelName] = useState('');
  const [channelScope, setChannelScope] = useState<'workspace' | 'space' | 'project'>('workspace');
  const [channelScopeId, setChannelScopeId] = useState<string>('');
  const [channelIcon, setChannelIcon] = useState<string>('hash');
  const [spaces, setSpaces] = useState<{ id: string; name: string }[]>([]);
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);

  // Reset state when dialog opens/tab changes
  useEffect(() => {
    if (open) {
      setTab(defaultTab);
      setSearch('');
      setSelectedDmMember(null);
      setSelectedGroupMembers([]);
      setGroupName('');
      setChannelName('');
      setChannelScope('workspace');
      setChannelScopeId('');
      setChannelIcon('hash');
    }
  }, [open, defaultTab]);

  // Load members
  useEffect(() => {
    if (!open || !currentOrganization?.id) return;
    fetchOrganizationMembers(currentOrganization.id, 'active')
      .then((data) => {
        setMembers(
          data
            .filter((m) => m.id !== currentMemberId)
            .map((m) => ({
              member_id: m.id,
              full_name: m.profile?.full_name || null,
              avatar_url: m.profile?.avatar_url || null,
              email: m.profile?.email || '',
            }))
        );
      })
      .catch(console.error);
  }, [open, currentOrganization?.id, currentMemberId]);

  // Load spaces & projects for channel tab
  useEffect(() => {
    if (!open || !currentOrganization?.id) return;
    fetchChannelScopes(currentOrganization.id)
      .then(({ spaces, projects }) => {
        setSpaces(spaces);
        setProjects(projects);
      })
      .catch(console.error);
  }, [open, currentOrganization?.id]);

  const filtered = useMemo(() => {
    if (!search) return members;
    const q = search.toLowerCase();
    return members.filter(
      m => m.full_name?.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
    );
  }, [members, search]);

  // ─── HANDLERS ─────────────────────────────────────────────────────────
  const handleCreateDM = async (member: MemberOption) => {
    if (!currentOrganization?.id || loading) return;
    setLoading(true);
    try {
      const channel = await createDM(currentOrganization.id, member.member_id);
      setActiveChannel(channel.id);
      loadMessages(channel.id);
      onOpenChange(false);
    } catch (err) {
      console.error('Error creating DM:', err);
      toast.error('Erreur lors de la création de la conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!currentOrganization?.id || !currentMemberId || loading) return;
    if (selectedGroupMembers.length < 2) {
      toast.error('Sélectionnez au moins 2 membres pour un groupe');
      return;
    }
    const name = groupName.trim() || selectedGroupMembers.map(m => m.full_name || m.email.split('@')[0]).join(', ');
    setLoading(true);
    try {
      const channel = await createGroupConversation({
        orgId: currentOrganization.id,
        name,
        memberIds: [currentMemberId, ...selectedGroupMembers.map(m => m.member_id)],
        createdBy: currentMemberId,
      });
      await loadChannels(currentOrganization.id);
      setActiveChannel(channel.id);
      loadMessages(channel.id);
      onOpenChange(false);
      toast.success('Conversation de groupe créée');
    } catch (err) {
      console.error('Error creating group:', err);
      toast.error('Erreur lors de la création du groupe');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChannel = async () => {
    if (!currentOrganization?.id || !currentMemberId || loading || !isAdmin) return;
    if (!channelName.trim()) {
      toast.error('Le nom du canal est requis');
      return;
    }
    if (channelScope !== 'workspace' && !channelScopeId) {
      toast.error('Sélectionnez un espace ou un projet');
      return;
    }
    setLoading(true);
    try {
      const channel = await createNewChannel({
        orgId: currentOrganization.id,
        scopeType: channelScope,
        scopeId: channelScope === 'workspace' ? undefined : channelScopeId,
        name: channelName.trim(),
        createdBy: currentMemberId,
        icon: channelIcon !== 'hash' ? channelIcon : undefined,
      });
      setActiveChannel(channel.id);
      loadMessages(channel.id);
      onOpenChange(false);
      toast.success('Canal créé');
    } catch (err) {
      console.error('Error creating channel:', err);
      toast.error('Erreur lors de la création du canal');
    } finally {
      setLoading(false);
    }
  };

  const toggleGroupMember = (member: MemberOption) => {
    setSelectedGroupMembers(prev =>
      prev.find(m => m.member_id === member.member_id)
        ? prev.filter(m => m.member_id !== member.member_id)
        : [...prev, member]
    );
  };

  // ─── MEMBER ROW ────────────────────────────────────────────────────────
  const MemberRow = ({ member, onClick, selected }: { member: MemberOption; onClick: () => void; selected?: boolean }) => {
    const name = member.full_name || member.email || '??';
    const initials = name.slice(0, 2).toUpperCase();
    const hue = Array.from(name).reduce((h, c) => c.charCodeAt(0) + ((h << 5) - h), 0) % 360;
    return (
      <button
        disabled={loading}
        className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-left transition-colors hover:bg-muted/60 disabled:opacity-50"
        onClick={onClick}
      >
        <Avatar className="h-7 w-7">
          <AvatarImage src={member.avatar_url || undefined} />
          <AvatarFallback
            className="text-[10px] font-semibold text-white"
            style={{ backgroundColor: `hsl(${Math.abs(hue)}, 55%, 50%)` }}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[12.5px] font-medium truncate text-foreground">
            {member.full_name || 'Sans nom'}
          </span>
          <span className="text-[11px] truncate text-muted-foreground">
            {member.email}
          </span>
        </div>
        {selected !== undefined && (
          <div className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
            selected ? 'bg-primary border-primary' : 'border-border'
          }`}>
            {selected && <Check className="h-3 w-3 text-primary-foreground" />}
          </div>
        )}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="text-sm font-semibold">Nouvelle conversation</DialogTitle>
          <DialogDescription className="text-[11px] text-muted-foreground">
            Créez une conversation directe, de groupe, ou un canal.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => { setTab(v as CreationType); setSearch(''); }} className="flex flex-col">
          <TabsList className="mx-5 mb-3 h-8 bg-muted/50 p-0.5">
            <TabsTrigger value="dm" className="text-[11px] h-7 gap-1 px-3">
              <MessageCircle className="h-3 w-3" /> 1:1
            </TabsTrigger>
            <TabsTrigger value="group" className="text-[11px] h-7 gap-1 px-3">
              <Users className="h-3 w-3" /> Groupe
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="channel" className="text-[11px] h-7 gap-1 px-3">
                <Hash className="h-3 w-3" /> Canal
              </TabsTrigger>
            )}
          </TabsList>

          {/* ─── DM TAB ──────────────────────────────────────────────── */}
          <TabsContent value="dm" className="mt-0 px-5 pb-5">
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un membre..."
                className="pl-8 h-8 text-[12px]"
                autoFocus
              />
            </div>
            <ScrollArea className="max-h-56">
              {filtered.length === 0 ? (
                <p className="text-center text-[11px] py-6 text-muted-foreground">
                  {members.length === 0 ? "Aucun autre membre" : "Aucun résultat"}
                </p>
              ) : (
                <div className="space-y-0.5">
                  {filtered.map(m => (
                    <MemberRow key={m.member_id} member={m} onClick={() => handleCreateDM(m)} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* ─── GROUP TAB ───────────────────────────────────────────── */}
          <TabsContent value="group" className="mt-0 px-5 pb-5">
            <Input
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              placeholder="Nom du groupe (optionnel)"
              className="h-8 text-[12px] mb-2"
            />

            {/* Selected chips */}
            {selectedGroupMembers.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedGroupMembers.map(m => (
                  <span
                    key={m.member_id}
                    className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-primary/10 text-primary text-[11px] font-medium"
                  >
                    {m.full_name || m.email.split('@')[0]}
                    <button onClick={() => toggleGroupMember(m)} className="hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un membre..."
                className="pl-8 h-8 text-[12px]"
              />
            </div>
            <ScrollArea className="max-h-44">
              <div className="space-y-0.5">
                {filtered.map(m => (
                  <MemberRow
                    key={m.member_id}
                    member={m}
                    onClick={() => toggleGroupMember(m)}
                    selected={!!selectedGroupMembers.find(s => s.member_id === m.member_id)}
                  />
                ))}
              </div>
            </ScrollArea>
            <Button
              size="sm"
              className="w-full mt-3 h-8 text-[12px]"
              disabled={selectedGroupMembers.length < 2 || loading}
              onClick={handleCreateGroup}
            >
              Créer le groupe ({selectedGroupMembers.length} membres)
            </Button>
          </TabsContent>

          {/* ─── CHANNEL TAB ─────────────────────────────────────────── */}
          <TabsContent value="channel" className="mt-0 px-5 pb-5 space-y-3">
            <Input
              value={channelName}
              onChange={e => setChannelName(e.target.value)}
              placeholder="Nom du canal"
              className="h-8 text-[12px]"
              autoFocus
            />

            {/* Icon picker */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium text-muted-foreground">Icône</label>
              <div className="grid grid-cols-10 gap-1">
                {CHANNEL_ICONS.map(({ name, icon: IconComp, label }) => (
                  <button
                    key={name}
                    type="button"
                    title={label}
                    className={cn(
                      "h-7 w-7 rounded-md flex items-center justify-center transition-all",
                      channelIcon === name
                        ? "bg-primary text-primary-foreground ring-2 ring-primary/30"
                        : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                    onClick={() => setChannelIcon(name)}
                  >
                    <IconComp className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-medium text-muted-foreground">Portée</label>
              <Select value={channelScope} onValueChange={(v: any) => { setChannelScope(v); setChannelScopeId(''); }}>
                <SelectTrigger className="h-8 text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="workspace">Workspace</SelectItem>
                  <SelectItem value="space">Espace</SelectItem>
                  <SelectItem value="project">Projet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {channelScope === 'space' && (
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-muted-foreground">Espace</label>
                <Select value={channelScopeId} onValueChange={setChannelScopeId}>
                  <SelectTrigger className="h-8 text-[12px]">
                    <SelectValue placeholder="Sélectionner un espace" />
                  </SelectTrigger>
                  <SelectContent>
                    {spaces.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {channelScope === 'project' && (
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-muted-foreground">Projet</label>
                <Select value={channelScopeId} onValueChange={setChannelScopeId}>
                  <SelectTrigger className="h-8 text-[12px]">
                    <SelectValue placeholder="Sélectionner un projet" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              size="sm"
              className="w-full h-8 text-[12px]"
              disabled={!channelName.trim() || (channelScope !== 'workspace' && !channelScopeId) || loading}
              onClick={handleCreateChannel}
            >
              Créer le canal
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ─── ICON MAPPING (exported for sidebar usage) ──────────────────────────
export const CHANNEL_ICON_MAP: Record<string, LucideIcon> = Object.fromEntries(
  CHANNEL_ICONS.map(i => [i.name, i.icon])
);